'use client';

import type { SoftphoneProvider } from '@poseidon/components/Softphone/softphoneClient.types';
import { CALL_LIMIT_RECHECK_MS, carrierUsesOurCallerNumber } from '@poseidon/components/Softphone/callingSetup';
import { ClickToCallButtonPure } from '@poseidon/components/Softphone/ClickToCallButtonPure';
import { resolveCallNumber } from '@poseidon/components/Softphone/resolveCallNumber';

import { api } from '@/utils/api';
import { useSoftphoneLauncher } from '~/components/SoftphoneProvider';

export interface ClickToCallButtonProps {
  teamId: string;
  /** Public contact id (contact_public_id). */
  contactId: string;
  contactName?: string;
  phoneNumber?: string | null;
  /**
   * The contact's org ("firm") phone. Dialed as a fallback (and labeled a firm number) when the
   * contact has no direct number of their own — `contactOrg?.primaryFields?.phone?.data`.
   */
  orgPhoneNumber?: string | null;
  /** Contact photo for the dialer avatar; initials fallback when absent. */
  imageSrc?: string | null;
  /** Team feature flag — when false the affordance is hidden entirely. */
  enabled: boolean;
  /**
   * Whether the rep has a caller number provisioned. When false (and `enabled` is true), the
   * button renders greyed/disabled with a tooltip instead of disappearing.
   */
  callingConnected: boolean;
  /** The team's carrier; picks the matching softphone client (Telnyx vs Twilio). */
  provider: SoftphoneProvider;
  /**
   * When placing the call for a cadence `call` step, the cadence_step id. Threaded to
   * `startCall` so logging the outcome completes that step server-side.
   */
  cadenceStepId?: string;
  /**
   * When placing the call for a standalone scheduled `call` reminder (no cadence step), the
   * global_reminder id. Threaded so logging the outcome clears the reminder.
   */
  globalReminderId?: string;
  /** Called after a call outcome is logged (e.g. to invalidate the contact / reminder caches). */
  onCallLogged?: () => void;
  className?: string;
}

/**
 * Dashboard click-to-call trigger. A thin button that hands the contact off to the app-global
 * `SoftphoneProvider` (mounted in `_app.tsx`), which owns the actual call + dialer so it survives
 * navigation. The carrier is chosen server-side and passed in as `provider`, so this button is the
 * same for Telnyx and Twilio — only the client the provider builds differs.
 */
export function ClickToCallButton({
  teamId,
  contactId,
  contactName,
  phoneNumber,
  orgPhoneNumber,
  imageSrc,
  enabled,
  callingConnected,
  provider,
  cadenceStepId,
  globalReminderId,
  onCallLogged,
  className,
}: ClickToCallButtonProps) {
  const { startCall, isBusy } = useSoftphoneLauncher();
  // Warn before the dial, not after it: the same read-only plan startCall runs says whether today's
  // call limits would refuse this contact. Same query key as the page's "Calling from" caption, so
  // it costs no extra request there, and the softphone refreshes it after every call. Re-checked
  // while blocked so the button comes back by itself when an admin raises a limit or the day turns.
  // JustCall picks the number in its own dialer — nothing of ours to limit.
  const dialInfoQuery = api.voiceCall.getContactDialInfo.useQuery(
    { teamId, contactId },
    {
      enabled: enabled && callingConnected && carrierUsesOurCallerNumber(provider),
      refetchInterval: (query) => (query.state.data?.callBlockedReason != null ? CALL_LIMIT_RECHECK_MS : false),
    },
  );
  // Fall back to the org's number (labeled a "firm number") when the person has none of their own —
  // the server's startCall resolves the same fallback independently for the actual dial.
  const { phoneNumber: dialNumber, isFirmNumber } = resolveCallNumber({
    personalPhone: phoneNumber,
    orgPhone: orgPhoneNumber,
  });

  return (
    <ClickToCallButtonPure
      enabled={enabled}
      callingConnected={callingConnected}
      phoneNumber={dialNumber}
      isFirmNumber={isFirmNumber}
      callBlockedReason={dialInfoQuery.data?.callBlockedReason}
      busy={isBusy}
      onCall={() =>
        startCall({
          teamId,
          contactId,
          contactName,
          phoneNumber: dialNumber,
          isFirmNumber,
          imageSrc,
          provider,
          cadenceStepId,
          globalReminderId,
          onCallLogged,
        })
      }
      className={className}
    />
  );
}
